import z from "zod";

export const ConversationVariableSchema = z.object({
    name: z.string(),
    value: z.string(),
});

export const TextSchema = z.object({
    type: z.literal("text"),
    content: z.string(),
});

export const GuestTextSchema = z.object({
    type: z.literal("guestText"),
    content: z.string(),
    speaker: z.string(),
});

export const RewardSchema = z.object({
    type: z.literal("reward"),
    name: z.string(),
    donationId: z.string(),
});

export const LinkSchema = z.object({
    type: z.literal("link"),
    url: z.string().url(),
    label: z.string(),
});

export const VariableSchema = z.object({
    type: z.literal("variable"),
    name: z.string(),
    value: z.string(),
});

export const BodySchema = z.union([
    TextSchema,
    GuestTextSchema,
    RewardSchema,
    LinkSchema,
    VariableSchema,
]);

export const VoucherStatusSchema = z.enum([
    "VOUCHER_REDEEMED",
    "VOUCHER_NOT_REDEEMED",
]);

export const VoucherSchema = z.object({
    token: z.string().optional(),
});

export const ConversationSchema = z.object({
    name: z.string(),
    conversation: z
        .object({
            start: z.object({
                sectionId: z.string(),
                title: z.string(),
            }),
            sections: z.array(
                z.object({
                    id: z.string(),
                    body: z.array(BodySchema),
                    questions: z
                        .array(
                            z.object({
                                text: z.string(),
                                sectionId: z.string().optional(),
                            }),
                        )
                        .optional(),
                    condition: z
                        .object({
                            variable: z.string(),
                            value: z.string(),
                        })
                        .optional(),
                    pollId: z.string().optional(),
                    pollOptions: z
                        .array(
                            z.object({
                                text: z.string(),
                                optionId: z.string(),
                            }),
                        )
                        .optional(),
                }),
            ),
            rewards: z
                .record(
                    z.string(),
                    z.object({
                        name: z.string(),
                    }),
                )
                .optional(),
            variables: z.array(ConversationVariableSchema),
        })
        .nullable(),
    character: z.string(),
});

export const ConversationDataSchema = z.object({
    conversation: ConversationSchema,
});

export const Date1EmotionSchema = z.enum([
    "Neutral",
    "AboutToCry",
    "Angry",
    "Charred",
    "Dancing",
    "Drool",
    "Excited",
    "ExtremeBlush",
    "PuppyEyes",
    "Sad",
    "Shy",
    "Smile",
    "Sweating",
    "Thinking",
    "VeryHappy",
    "Clone",
]);

const date1Emotions = Date1EmotionSchema.options;

export const Date2EmotionSchema = z.enum([
    "Neutral",
    "Leaning",
    "Flirty",
    "Laughing",
    "Confused",
    "Cocky",
    "Disappointed",
    "Excited",
    "Bored",
    "Heartbroken",
    "Shooting",
    "Clone",
]);

const date2Emotions = Date2EmotionSchema.options;

export const Date3EmotionSchema = z.enum([
    "Neutral",
    "Angry",
    "Blush",
    "Clone",
    "Confident",
    "Confused",
    "Distracted",
    "Flirty",
    "FlirtySmile",
    "Overexcited",
    "Sad",
    "Smile",
    "Surprised",
    "Suspicious",
    "Thinking",
    "VeryFlirty",
    "VeryHappy",
]);

const date3Emotions = Date3EmotionSchema.options;

export const Date4EmotionSchema = z.enum([
    "Neutral",
    "Bored",
    "Clone",
    "Confused",
    "Disgusted",
    "Diva",
    "Drooling",
    "Excited",
    "Flirty",
    "InLove",
    "Irritated",
    "Sassy",
    "Shy",
    "Smile",
    "Suspicious",
]);

const date4Emotions = Date4EmotionSchema.options;

export const Date5EmotionSchema = z.enum([
    "Neutral",
    "Blushed",
    "Confused",
    "EvilPose",
    "Exhausted",
    "GrandiosePose",
    "Irritated",
    "Sassy",
    "Satisfied",
    "Smile",
    "Suspicious",
    "Thinking",
]);

const date5Emotions = Date5EmotionSchema.options;

export const Date6EmotionSchema = z.enum([
    "Neutral",
    "Clone",
    "Shrugging",
    "Fire",
]);

const date6Emotions = Date6EmotionSchema.options;

export const emotions = {
    date1: date1Emotions,
    date2: date2Emotions,
    date3: date3Emotions,
    date4: date4Emotions,
    date5: date5Emotions,
    date6: date6Emotions,
};

export const EmotionSchema = z.union([
    Date1EmotionSchema,
    Date2EmotionSchema,
    Date3EmotionSchema,
    Date4EmotionSchema,
    Date5EmotionSchema,
    Date6EmotionSchema,
]);

export const CachedVouchers = z.object({}).catchall(z.string());
export const CachedVoucherStatuses = z.object({}).catchall(VoucherStatusSchema);
